import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key } from '@shared/api/query-keys';
import { unreadCountKey } from '@shared/app-shell/queries';

import type { DeleteResult, MarkResult } from './types';

/**
 * The three writes this surface performs, and the only three the product has
 * against an inbox row.
 *
 * **The handle and the table are one number in two places, and they move
 * together.** Every mutation answers with the fresh `unread_count`, so the
 * badge is written from the response in the same pass rather than left to a
 * refetch that could land after the operator has looked away. The list is
 * invalidated behind it, and the count query with it, so a second tab's write
 * still reconciles.
 *
 * **Mutations never auto-retry** — the platform rule, and it binds here for the
 * ordinary reason: a repeat of a bulk action whose outcome is unknown is the
 * operator's decision, not the client's. Retry safety on this service comes
 * from the shape of the writes themselves: `UPDATE … WHERE read_at IS NULL`
 * and `WHERE deleted_at IS NULL` are idempotent by construction, and a repeat
 * answers `200` with a count of zero.
 *
 * **`Idempotency-Key` is not sent.** The header is scoped platform-wide to
 * finance-bound money-moving writes, and this service accepts it on no
 * endpoint; sending it would document a contract that does not exist.
 */

/** The list, whatever sort and cursor it is currently holding. */
function notificationsPrefix() {
  return key('notification', 'notifications');
}

function settleUnread(queryClient: QueryClient, unreadCount: number) {
  queryClient.setQueryData(unreadCountKey(), { unread_count: unreadCount });
  void queryClient.invalidateQueries({ queryKey: notificationsPrefix() });
  void queryClient.invalidateQueries({ queryKey: unreadCountKey() });
}

export function useMarkRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => api.post<MarkResult>('notification', '/notifications:markRead', { ids }),
    onSuccess: (result) => settleUnread(queryClient, result.unread_count),
  });
}

export function useMarkUnread() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => api.post<MarkResult>('notification', '/notifications:markUnread', { ids }),
    onSuccess: (result) => settleUnread(queryClient, result.unread_count),
  });
}

/**
 * Delete carries **no confirmation**, and that is the one place this console's
 * confirm-before-destroy posture does not reach. That posture governs a
 * management table whose row *is* the record; a notification is a copy of
 * something that already happened, and removing it destroys no subject. What
 * the operator loses is a line in their own inbox.
 */
export function useDeleteNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => api.post<DeleteResult>('notification', '/notifications:delete', { ids }),
    onSuccess: (result) => settleUnread(queryClient, result.unread_count),
  });
}
